import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import uniq from 'lodash/uniq'
import {useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'

import BuildLineLayout from '../../../../../containers/BuildLineLayout'
import OneLineDetector from '../../../../../containers/OneLineDetector'
import {useSyncedBooleanQueryParam} from '../../../../../hooks/routes'
import {getBuildsReady, makeGetBuilds} from '../../../../../selectors'
import {emptyArray} from '../../../../../utils/empty'
import {notNull} from '../../../../../utils/guards'
import {UserProperties} from '../../../../../utils/userProperties'
import {getBuildsHash, sortBuildTypeIds} from '../ChangeDetailsTabs.utils'

import type {ChangeBuilds_buildTypes$key} from './__generated__/ChangeBuilds_buildTypes.graphql'
import ChangeBuildsFlatList from './ChangeBuildsFlatList'
import ChangeBuildsTree from './ChangeBuildsTree'

import styles from './ChangeBuilds.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  buildsLocator: string
  isDeployment?: boolean
  buildTypesKey: ChangeBuilds_buildTypes$key | null
}

const buildTypesFragment = graphql`
  fragment ChangeBuilds_buildTypes on BuildTypes {
    buildType {
      rawId
    }
  }
`

function ChangeBuilds({buildsLocator, isDeployment, buildTypesKey}: Props) {
  const buildTypes = useFragment(buildTypesFragment, buildTypesKey)?.buildType ?? emptyArray
  const buildTypeIds = useMemo(
    () => uniq(buildTypes.map(buildType => buildType.rawId).filter(notNull)),
    [buildTypes],
  )

  const getBuilds = useMemo(makeGetBuilds, [])

  const [hideBuildTypes, setHideBuildTypes] = useSyncedBooleanQueryParam({
    param: 'onlyFailed',
    userProperty: UserProperties.CHANGE_BUILDS_SHOW_ONLY_FAILED,
  })
  const [showHierarchy, setShowHierarchy] = useSyncedBooleanQueryParam({
    param: 'showHierarchy',
    userProperty: UserProperties.CHANGE_BUILDS_SHOW_HIERARCHY,
  })

  const builds = useAppSelector(state => getBuilds(state, buildsLocator))
  const buildsReady = useAppSelector(state => getBuildsReady(state, buildsLocator))

  const buildsHash = useMemo(() => getBuildsHash(builds), [builds])
  const filteredBuildTypeIds = useMemo(
    () =>
      hideBuildTypes
        ? buildTypeIds.filter(buildTypeId => {
            const buildTypeBuilds = buildsHash[buildTypeId]
            return buildTypeBuilds?.some(
              build => build.status !== 'SUCCESS' || build.state === 'running',
            )
          })
        : buildTypeIds,
    [buildTypeIds, buildsHash, hideBuildTypes],
  )
  const sortedBuildTypeIds = useMemo(
    () => sortBuildTypeIds(filteredBuildTypeIds),
    [filteredBuildTypeIds],
  )

  return buildsReady ? (
    <div>
      <Checkbox
        containerClassName={styles.filterItem}
        checked={showHierarchy}
        onChange={e => setShowHierarchy(e.currentTarget.checked)}
      >
        {'Show projects hierarchy'}
      </Checkbox>
      <Checkbox
        containerClassName={styles.filterItem}
        checked={hideBuildTypes}
        onChange={e => setHideBuildTypes(e.currentTarget.checked)}
      >
        {'Hide successfully finished and not triggered configurations'}
      </Checkbox>
      <OneLineDetector>
        <BuildLineLayout showPath={false}>
          {showHierarchy ? (
            <ChangeBuildsTree
              buildTypeIds={filteredBuildTypeIds}
              buildsHash={buildsHash}
              isDeployment={isDeployment}
            />
          ) : (
            <ChangeBuildsFlatList
              buildTypeIds={sortedBuildTypeIds}
              buildsHash={buildsHash}
              isDeployment={isDeployment}
            />
          )}
          {sortedBuildTypeIds.length === 0 && (
            <span className={styles.emptyList}>{`${
              isDeployment ? 'Deployments' : 'Builds'
            } list is empty`}</span>
          )}
        </BuildLineLayout>
      </OneLineDetector>
    </div>
  ) : (
    <LoaderInline>{'Loading...'}</LoaderInline>
  )
}

export default ChangeBuilds
